import { useCallback, useEffect, useState } from 'react';
import { REQUIRED_API_VERSION } from '../services/api';

/** Amber banner + button — same copy as workspace customer engagement. */
export function EngagementBackendBanner({ state, onRefresh, busy, note }) {
  if (state === 'ok' || state === 'checking') return null;
  const outdated = state === 'outdated';
  return (
    <div className="portal-card portal-card--pad-sm border-amber-200 bg-amber-50 text-sm text-amber-950">
      <strong>
        {outdated
          ? 'Customer engagement needs a service refresh.'
          : 'Customer engagement is temporarily unavailable.'}
      </strong>
      <p className="mt-1">
        {outdated
          ? 'This portal is newer than the engagement service that is currently running. Refresh the service, then this page will reconnect.'
          : 'The engagement service did not respond. Refresh it, then this page will reconnect.'}
      </p>
      <button
        type="button"
        className="mt-3 rounded-lg bg-[#1f1630] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        disabled={busy}
        onClick={onRefresh}
      >
        {busy ? 'Refreshing service…' : 'Refresh engagement service'}
      </button>
      {note ? <p className="mt-2 text-xs">{note}</p> : null}
    </div>
  );
}

export function evaluateEngagementHealth(h) {
  const versionOk = (h?.api_version || 0) >= REQUIRED_API_VERSION;
  const productOk = h?.product === 'naturals_engagement';
  const db = String(h?.database || '').toLowerCase();
  const dbOk =
    h?.database_ok !== false && (!db || (db !== 'sqlite' && !db.includes('demo')));
  return versionOk && productOk && dbOk ? 'ok' : 'outdated';
}

/** Poll /health and POST recover (workspace salon-admin or naturals-portal API). */
export function useEngagementServiceRecover({ healthUrl, recoverRequest }) {
  const [backend, setBackend] = useState('checking');
  const [recovering, setRecovering] = useState(false);
  const [recoverNote, setRecoverNote] = useState('');

  const checkBackend = useCallback(async () => {
    try {
      const res = await fetch(healthUrl, { credentials: 'include' });
      const h = await res.json().catch(() => ({}));
      if (!res.ok) {
        setBackend('down');
        return 'down';
      }
      const next = evaluateEngagementHealth(h);
      setBackend(next);
      return next;
    } catch {
      setBackend('down');
      return 'down';
    }
  }, [healthUrl]);

  const refreshService = useCallback(async () => {
    setRecovering(true);
    setRecoverNote('');
    try {
      const { ok, note } = await recoverRequest();
      if (!ok) {
        setRecoverNote(note || 'The engagement service could not be refreshed.');
        return;
      }
      setRecoverNote('Service refresh started. Reconnecting…');
      for (let i = 0; i < 8; i += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const next = await checkBackend();
        if (next === 'ok') {
          setRecoverNote('');
          return;
        }
      }
      setRecoverNote('The service is still starting. Reload this page in a moment.');
    } catch {
      setRecoverNote('The refresh could not be completed. Try again in a moment.');
    } finally {
      setRecovering(false);
    }
  }, [checkBackend, recoverRequest]);

  useEffect(() => {
    checkBackend();
  }, [checkBackend]);

  return { backend, recovering, recoverNote, refreshService, checkBackend };
}
